import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { ENV } from '@environment';
import _ from 'lodash';
import { RootState } from 'store';

import FiltersBottomSheet, { FilterItem, PriceFilter, Select } from 'components/FiltersBottomSheet';
import Loader from 'components/Loader';
import { Mobile, Desktop } from 'components/Responsive';
import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { HotelFilterResponse, HotelFilter } from 'models/hotelFilter';
import { TravelHotelContentLocationRequest } from 'models/hotelLocation';
import { HotelsResultRequestOrderCriteriaEnum } from 'models/hotelsSearch';
import { amplitudelogEvent, FilterGroupEvent, SelectFilterEvent } from 'services/amplitudeService';
import { HotelAmenities } from 'services/hotelService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount } from 'utils/generic';

import LoaderDesktop from './components/LoaderDesktop';
import Name from './components/Name';
import Order from './components/Order';
import Stars from './components/Stars';
import { City } from 'models/city';
import TravelPolicies from 'components/FlightsFilters/components/TravelPolicies';
import IncludedTaxes from './components/IncludedTaxes';
import Scores from './components/Scores';

interface Props {
  show: boolean;
  currency: string;
  defaultFilters?: HotelFilter;
  availableFilters?: HotelFilterResponse;
  amenitiesCodes: HotelAmenities[];
  onSearch: (filters: HotelFilter) => void;
  onClose: () => void;
  destinationPoint?: TravelHotelContentLocationRequest | City;
  checkInDate?: Date;
  checkOutDate?: Date;
  isLoading: boolean;
}

const DEFAULT_ORDER = HotelsResultRequestOrderCriteriaEnum.RECOMENDATION;
const TIMEOUT_INTERVAL = 700;

export const HotelsFilters = ({
  defaultFilters,
  availableFilters,
  amenitiesCodes,
  currency,
  onSearch,
  onClose,
  destinationPoint,
  checkInDate,
  checkOutDate,
  isLoading,
  ...props
}: Props) => {
  const isDesktop = useIsDesktop();
  const { t } = useStoreTranslation();
  const [searchTimeout, setSearchTimeout] = useState<number>();

  const [orderCriteria, setOrderCriteria] = useState(defaultFilters?.orderCriteria || DEFAULT_ORDER);
  const [categories, setCategories] = useState<string[]>(defaultFilters?.categories || []);
  const [price, setPrice] = useState<number[]>(
    defaultFilters?.priceFrom && defaultFilters.priceTo ? [defaultFilters?.priceFrom, defaultFilters.priceTo] : [],
  );
  const [hotelName, setHotelName] = useState(defaultFilters?.hotelName || '');
  const [zones, setZones] = useState<string[]>(defaultFilters?.zones || []);
  const [amenities, setAmenities] = useState<string[]>(defaultFilters?.ammenities || []);
  const [score, setScore] = useState<string>();
  const [boards, setBoards] = useState<string[]>(defaultFilters?.boards || []);
  const [chains, setChains] = useState<string[]>(defaultFilters?.chains || []);
  const [refundableType, setRefundableType] = useState<string | undefined>(defaultFilters?.refundableType);
  const [travelPolicies, setTravelPolicies] = useState<string[]>(defaultFilters?.travelPolicies || []);
  const [includedTaxes, setIncludedTaxes] = useState<string[]>(defaultFilters?.includedTaxes || []);

  const [minMax, setMinMax] = useState<number[]>(
    defaultFilters?.priceFrom && defaultFilters.priceTo ? [defaultFilters?.priceFrom, defaultFilters.priceTo] : [],
  );
  const [filtersCount, setFiltersCount] = useState(0);
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (availableFilters && (!defaultFilters?.priceFrom || !defaultFilters?.priceTo)) {
      setPrice([availableFilters.minPrice, availableFilters.maxPrice]);

      if (availableFilters.minPrice && availableFilters.maxPrice) {
        setMinMax([availableFilters.minPrice, availableFilters.maxPrice]);
      }
    }
  }, [availableFilters, defaultFilters]);

  const search = useSelector((state: RootState) => state.hotels.search);
  const user = useSelector((state: RootState) => state.user.accountResult);

  const destinationId = (() => {
    if (!destinationPoint) return '';

    if ('id' in destinationPoint) {
      return destinationPoint.id.toString();
    } else {
      return destinationPoint?.code.toString();
    }
  })();

  const sendAmplitudeEvent = (filterGroup: FilterGroupEvent, filterValue: any) => {
    const amplitudeSelectFilter: SelectFilterEvent = {
      FILTER_GROUP: filterGroup,
      FILTER_VALUE: filterValue.toString(),
      TRAVEL_PRODUCT_TYPE: 'HOTELS',
      TRAVEL_FROM: '',
      TRAVEL_TO: destinationId ?? '',
      TRAVEL_DESTINATION_TYPE: '',
      TRAVEL_TRIP_TYPE: '',
      TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), checkInDate).toString(),
      TRAVEL_DATE_FROM: checkInDate?.toString() ?? '',
      TRAVEL_DATE_TO: checkOutDate?.toString() ?? '',
      TRAVEL_TRIP_LENGTH: daysCount(checkInDate, checkOutDate).toString(),
      TRAVEL_PASSENGERS: '1',
      TRAVEL_ADULTS: '1',
      TRAVEL_CHILDREN: '0',
      TRAVEL_INFANTS: '0',
      TRAVEL_CLASS_OF_SERVICE: '',
      TRAVEL_ROOMS: '1',
      TRAVEL_WITH_BAGGAGE: '',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: search?.searchId ?? '',
    };

    amplitudelogEvent(amplitudeEvents.SELECT_FILTER, amplitudeSelectFilter, user);
  };

  useEffect(() => {
    let count = 0;
    if (orderCriteria !== DEFAULT_ORDER) {
      count++;
    }

    if (categories && categories.length > 0) {
      count += _.uniq(categories).length;
    }

    if (price && minMax && (price.first() !== minMax.first() || price.last() !== minMax.last())) {
      count++;
    }

    if (hotelName && hotelName.length > 0) {
      count++;
    }

    if (zones && zones.length > 0) {
      count++;
    }

    if (boards && boards.length > 0) {
      count++;
    }

    if (chains && chains.length > 0) {
      count++;
    }
    
    if (score && score.length > 0) {
      count++;
    }

    if (includedTaxes && includedTaxes.length > 0) {
      count++;
    }

    if (amenities && amenities.length > 0) {
      count++;
    }

    if (refundableType) {
      count++;
    }

    setFiltersCount(count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amenities, categories, hotelName, minMax, refundableType, orderCriteria, price, zones, boards, chains, score, includedTaxes, travelPolicies]);

  const handleSearch = useCallback(() => {
    const filter: HotelFilter = {};
    if (orderCriteria) filter.orderCriteria = orderCriteria;
    if (categories.length > 0) filter.categories = categories;
    filter.hotelName = hotelName;
    if (price.first() !== minMax.first() || price.last() !== minMax.last()) {
      filter.priceFrom = price.first();
      filter.priceTo = price.last();
    }
    if (zones) filter.zones = zones;
    if (boards) filter.boards = boards;
    if (chains) filter.chains = chains;
    if (score) filter.score = score;
    if (includedTaxes) filter.includedTaxes = includedTaxes;
    if (amenities) filter.ammenities = amenities;
    if (refundableType) filter.refundableType = refundableType;
    if(travelPolicies.length > 0) filter.travelPolicies = travelPolicies;

    onSearch(filter);
  }, [amenities, categories, hotelName, minMax, onSearch, orderCriteria, price, refundableType, zones, boards, chains, score, includedTaxes, travelPolicies]);

  const getTranslatedRefundableOption = (text: string) => {
    if (text === 'Refundable') return t('hotelsFilters.cancelPolicy.refundable');
    if (text === 'NonRefundable') return t('hotelsFilters.cancelPolicy.nonRefundable');
    return text;
  };

  const handleSearchName = (value: string) => {
    setHotelName(value);

    if (value.length === 0 || value.length > 2) {
      let timeoutId = searchTimeout;
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        if (isDesktop) setChanged(true);
        sendAmplitudeEvent(FilterGroupEvent.HOTEL_NAME, value);
      }, TIMEOUT_INTERVAL);
      setSearchTimeout(timeoutId);
    }
  };

  const handleSearchPrice = (values: number[]) => {
    setPrice(values);
    let timeoutId = searchTimeout;
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      if (isDesktop) setChanged(true);
      sendAmplitudeEvent(FilterGroupEvent.PRICE, values);
    }, TIMEOUT_INTERVAL);
    setSearchTimeout(timeoutId);
  };

  useEffect(() => {
    if (isDesktop && changed) {
      handleSearch();
      setChanged(false);
    }
  }, [changed, handleSearch, isDesktop]);

  const handleRefundableImageEvent = () => {
    if (refundableType === undefined) {
      setRefundableType('Refundable');
      if (isDesktop) setChanged(true);
      return;
    }

    if (refundableType.toLowerCase() === 'refundable') {
      setRefundableType(undefined);
    } else {
      setRefundableType('Refundable');
    }

    if (isDesktop) setChanged(true);
    return;
  };

  const filterTravelPolicies = [
    {
      'text': 'withinPolicies'
    },
    {
      'text': 'outOfPolicies'
    }
  ]

  const filterIncludedTaxes = [
    {
      'text': 'yes'
    },
    {
      'text': 'no'
    }
  ]

  const renderContent = (filters: HotelFilterResponse) => (
    <>
      
      <FilterItem
        name={t('flightsFilters.travelPolicies.title')}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <TravelPolicies
          values={travelPolicies}
          onChange={(value) => {
            setTravelPolicies(value);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.TRAVELPOLICIES, value);
          }}
          travelPolicies={filterTravelPolicies}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsCard.taxesTitle')}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <IncludedTaxes
          values={includedTaxes}
          onChange={(value) => {
            setIncludedTaxes(value);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.INCLUDEDTAXES, value);
          }}
          includedTaxes={filterIncludedTaxes}
        />
      </FilterItem>
      
      <FilterItem
        name={t('hotelsFilters.hotelName.title')}
        changed={hotelName && hotelName.length > 2 ? true : false}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Name value={hotelName} onChange={handleSearchName} />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.order.title')}
        changed={orderCriteria !== DEFAULT_ORDER}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Order
          value={orderCriteria}
          onChange={(value) => {
            if (orderCriteria !== value) sendAmplitudeEvent(FilterGroupEvent.ORDER, value);
            setOrderCriteria(value);
            if (isDesktop) setChanged(true);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.price.title')}
        changed={price.first() !== minMax.first() || price.last() !== minMax.last()}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <PriceFilter
          min={filters.minPrice}
          max={filters.maxPrice}
          value={price}
          currency={currency}
          onChange={handleSearchPrice}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.cancelPolicy.title')}
        changed={refundableType ? true : false}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={
            availableFilters?.nonRefundable.map((item) => ({
              ...item,
              label: getTranslatedRefundableOption(item.text),
            })) || []
          }
          values={refundableType ? [refundableType] : []}
          onChange={(value: string[]) => {
            setRefundableType(value[0]);
            if (isDesktop) setChanged(true);
          }}
          allowSelectOne
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.HOTEL_POLICIES, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.stars.title')}
        count={_.uniq(categories).length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Stars
          categories={filters.category}
          values={categories}
          onChange={(values) => {
            setCategories(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.STARS, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.zone.title')}
        count={_.uniq(zones).length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={availableFilters?.zones.map((zone) => ({ text: zone.Text, count: zone.Count })) || []}
          values={zones}
          onChange={(values) => {
            setZones(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.LOCATION, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.amenities.title')}
        count={_.uniq(amenities).length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={(availableFilters?.ammenities || [])
            .map((amenity) => ({
              ...amenity,
              label: amenitiesCodes.find((ac) => ac.code === amenity.text)?.name || '',
            }))
            .filter((amenity) => amenity.label)}
          values={amenities}
          onChange={(values) => {
            setAmenities(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked)
              sendAmplitudeEvent(FilterGroupEvent.AMENITIES, amenitiesCodes.find((ac) => ac.code === value)?.name);
          }}
        />
      </FilterItem>
      
      <FilterItem
        name={t('hotelsFilters.scores.title')}
        count={_.uniq(score).length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Scores
          scores={filters.score}
          values={score}
          onChange={(values) => {
            setScore(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.SCORES, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.boards.title')}
        count={_.uniq(boards).length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={availableFilters?.boards?.filter((board) => board.text).map((board) => ({ text: board.text, count: board.count })) || []}
          values={boards}
          onChange={(values) => {
            setBoards(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.BOARDS, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.chains.title')}
        count={_.uniq(chains).length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={availableFilters?.chains.map((chain) => ({ text: chain.text, count: chain.count })) || []}
          values={chains}
          onChange={(values) => {
            setChains(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.CHAINS, value);
          }}
        />
      </FilterItem>
    </>
  );

  const handleClearFilters = useCallback(() => {
    setOrderCriteria(DEFAULT_ORDER);
    setCategories([]);
    setPrice(minMax);
    setHotelName('');
    setZones([]);
    setScore('');
    setBoards([]);
    setChains([]);
    setAmenities([]);
    setTravelPolicies([]);
    setIncludedTaxes([]);
    setRefundableType(undefined);
  }, [minMax]);

  useEffect(() => {
    if (typeof defaultFilters === 'undefined') {
      handleClearFilters();
      return;
    }
    setOrderCriteria(defaultFilters?.orderCriteria ? defaultFilters.orderCriteria : DEFAULT_ORDER);
    setCategories(defaultFilters?.categories ? defaultFilters.categories : []);
    setPrice(
      defaultFilters?.priceFrom && defaultFilters.priceTo
        ? [defaultFilters?.priceFrom, defaultFilters.priceTo]
        : minMax,
    );
    setHotelName(defaultFilters?.hotelName ? defaultFilters.hotelName : '');
    setZones(defaultFilters?.zones ? defaultFilters.zones : []);
    setScore(defaultFilters?.score ? defaultFilters.score : '');
    setBoards(defaultFilters?.boards ? defaultFilters.boards : []);
    setChains(defaultFilters?.chains ? defaultFilters.chains : []);
    setAmenities(defaultFilters?.ammenities ? defaultFilters.ammenities : []);
    setRefundableType(defaultFilters?.refundableType ? defaultFilters.refundableType : '');
    setTravelPolicies(defaultFilters?.travelPolicies ? defaultFilters.travelPolicies : []);
    setIncludedTaxes(defaultFilters?.includedTaxes ? defaultFilters.includedTaxes : []);

  }, [defaultFilters, handleClearFilters, minMax]);

  return (
    <>
      <Mobile>
        <FiltersBottomSheet
          {...props}
          showSearchButton={Boolean(availableFilters)}
          onClose={onClose}
          onSearch={handleSearch}
          filtersCount={filtersCount}
          onClearFilter={handleClearFilters}
        >
          {!availableFilters ? <Loader fullScreen /> : renderContent(availableFilters)}
        </FiltersBottomSheet>
      </Mobile>
      <Desktop>{isLoading ? <LoaderDesktop /> : availableFilters && renderContent(availableFilters)}</Desktop>
    </>
  );
};

export default HotelsFilters;
